import React, { useState, useMemo, useRef, useCallback } from 'react'
import Icon from 'components/Icon'
import { HexColorPicker } from 'react-colorful'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { Link } from 'react-router-dom'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { request } from 'hooks/useApi'
import debounce from 'debounce-promise'
import { themes, DEFAULT_THEME } from 'themes'
import { Button } from '@arnsbogroup/react-voicearchive'
import { messages as routeMessages } from 'routes'
import projectMessages from 'containers/ProjectPage/messages'
import { useIntl } from 'react-intl'
import { isUrl, isEmail, getDomain } from 'utils'
import { useLocation, useNavigate } from 'react-router-dom'
import useUrl from 'useUrl'
import NiceModal from '@ebay/nice-modal-react'
import ProjectLinksModal from '../ProjectLinksModal' 

const ProjectSettings = ({
  dispatch,
  loading,
  onCreateCharacter,
  onChangeView,
  viewAsPublic,
  project,
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const [editing, setEditing] = useState(null)
  const [showBrands, setShowBrands] = useState(false)
  const container = useRef()
  const { formatMessage, locale } = useIntl()
  const location = useLocation()
  const navigate = useNavigate()

  useOnClickOutside(
    container,
    useCallback(() => {
      setEditing(null)
    })
  )

  const onShowLinks = () => {
    NiceModal.show(ProjectLinksModal, { project, location })
  }

  const onChange = (value) => {
    dispatch({
      type: 'EDIT_BRAND',
      payload: {
        key: 'theme',
        value: {
          ...project.brand.theme,
          [editing]: {
            ...project.brand.theme[editing],
            default: value,
          },
        },
      },
    })
  }

  const anonymize = () => {
    dispatch({
      type: 'PATCH',
      payload: {
        ...project,
        brand: {
          logo: null,
          banner: null,
          icon: null,
          theme: themes[DEFAULT_THEME],
        },
      },
    })
  }

  const initiateBrand = () => {
    const brand = {
      logo: 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg',
      banner:
        'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/banner.jpg',
      icon: 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/icon-blue.png',
      theme: themes[DEFAULT_THEME],
    }

    dispatch({ type: 'PATCH', payload: { ...project, brand } })
  }

  return [
    <div className="h-14 block w-full" />,
    <div
      className="fixed top-0 left-0 w-full border-b border-gray-200 shadow bg-white group z-50 shadow"
      ref={container}
      tabIndex={0}
    >

      {project?.brand?.customer_uid > 0 && (
        <div className="w-full font-medium text-yellow-600 text-xs p-2 bg-yellow-50 space-x-1">
          <span>You are using a protected customer brand!</span>
          <button
            className="underline font-medium"
            onClick={() => {
              dispatch({
                type: 'EDIT_BRAND',
                payload: { key: 'customer_uid', value: null },
              })
              dispatch({
                type: 'EDIT_BRAND',
                payload: { key: 'uid', value: null },
              })
            }}
          >
            Overwrite
          </button>
        </div>
      )}

      {editing && (
        <div className="top-20 left-4 z-50 bg-white absolute rounded-xl flex justify-center">
          <HexColorPicker
            color={project.brand.theme?.[editing].default}
            onChange={onChange}
          />
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap p-2">
        <div className="flex space-x-2 items-center">
          { project.brand?.theme && ['primary', 'secondary', 'tertiary'].map((key) => (
            <fieldset
              key={key}
              className="flex items-center h-8 rounded-sm bg-white overflow-hidden border border-2 border-gray-300"
            >
              <div
                className="w-8 h-8 block"
                style={{ backgroundColor: project.brand.theme?.[key]?.default }}
              />
              <input
                className="w-20 px-2 pt-1 text-gray-700 text-sm"
                onFocus={() => setEditing(key)}
                placeholder={`${key}`}
                value={project.brand.theme?.[key]?.default}
                onChange={({ target: { value } }) => onChange(value)}
              />
            </fieldset>
          )) }
        </div>

        <div className="flex space-x-2">
          <Button variant="text" size="sm" icon="share" onClick={onShowLinks} className="text-left">
            Share
          </Button>

          { !project?.brand?.theme &&
            <Button variant="text" size="sm" icon="pencil" onClick={initiateBrand} className="text-left">
              Customize theme
            </Button>
          }

          {showBrands && (
            <AsyncCreatableSelect
              placeholder="Lookup existing brand or fetch by domain, URL or e-mail"
              className="w-[500px]"
              value={
                project?.brand?.uid
                  ? { label: project.brand.name, value: project.brand.uid }
                  : null
              }
              onChange={({ brand }, op) => {
                if (op.action == 'create-option') {
                  const domain = getDomain(op.option.value)
                  dispatch({
                    type: 'PATCH',
                    payload: {
                      ...project,
                      brand: {
                        uid: op.option.value,
                        name: `Fetching brand for "${domain}"..`,
                      },
                    },
                  })
                  request(`/rest/v2/brand?domain=${domain}`, {
                    fetchOptions: { method: 'POST' },
                  })
                    .then(({ data }) => dispatch({
                      type: 'PATCH',
                      payload: { ...project, brand: data },
                    }))
                    .catch(() =>
                      dispatch({
                        type: 'PATCH',
                        payload: { ...project, brand: {} },
                      })
                    )
                } else if (brand) {
                  dispatch({ type: 'PATCH', payload: { ...project, brand } })
                }
              }}
              formatCreateLabel={(query) => 'Fetch brand for domain: "' + getDomain(query) + '"'}
              isValidNewOption={(query) => isUrl(query) || isEmail(query)}
              cacheOptions={true}
              loadOptions={debounce(
                (query) =>
                  request(
                    `/rest/v2/customer_brand${
                      query.length ? `?search[displayName]=${query}` : ''
                    }`
                  ).then((brands) =>
                    brands.map((brand) => ({
                      label: brand.name,
                      value: brand.uid,
                      brand: { ...brand, theme: JSON.parse(brand.theme) },
                    }))
                  ),
                200
              )}
            />
          )}

          <Button variant="text" size="sm" icon="search" onClick={() => setShowBrands(!showBrands)} className="text-left">
            {showBrands ? 'Hide' : 'Lookup theme'}
          </Button>

          <Link to="/">
            <Button variant="text" size="sm" icon="pencil" className="text-left w-full">
              Select voices
            </Button>
          </Link>

          <Button variant="text" size="sm" icon="user-astronaut" onClick={onCreateCharacter} className="text-left">
            Add character
          </Button>

          <Button variant="text" size="sm" icon="user-secret" onClick={anonymize} className="text-left">
            Anonymize
          </Button>

          {loading ? (
            <Button variant="outline" size="sm" disabled className="text-left">
              <Icon icon="spinner-third" className="animate-spin" /> Loading..
            </Button>
          ) : project.changed ? (
            <Button variant="outline" size="sm" icon="save" onClick={() => project.save()} className="text-left">
              Save changes
            </Button>
          ) : null}

          <Button size="sm" icon="eye" onClick={onChangeView} className="text-left">
            {viewAsPublic ? 'Edit' : 'View'}
          </Button>
        </div>
      </div>
    </div>
  ]
}

export default ProjectSettings
